import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { createOrder, previewPayment, releasePayment, updateOrder } from "./api";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatEuro,
  guaranteeLabels,
  orderStatusLabels,
  warningText
} from "./format";

const day = value => (value ? value.format("YYYY-MM-DD") : "");

/** The warnings of a compliance check, as a list. */
export const WarningList = ({ warnings }) => {
  const { t } = useTranslation("dashboard");
  return (
    <ul className="sc-warning-list">
      {(warnings || []).map(code => (
        <li key={code}>{warningText(t, code)}</li>
      ))}
    </ul>
  );
};

/**
 * Asks once before going ahead despite compliance warnings. Resolves true when the user
 * confirms; the server logs that the warnings were acknowledged.
 */
const confirmWarnings = (t, title, warnings) =>
  new Promise(resolve => {
    Modal.confirm({
      rootClassName: "subcontractors-page",
      title,
      content: <WarningList warnings={warnings} />,
      okText: t("Continue anyway"),
      cancelText: t("Cancel"),
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    });
  });

const isWarning = error => error?.response?.data?.code === "COMPLIANCE_WARNINGS";

/** Create or edit a Nachunternehmervertrag (summary). */
export const OrderModal = ({ open, sub, meta, order, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const money = decimalInputProps(i18n.language);
  const kinds = guaranteeLabels(t);
  const statuses = orderStatusLabels(t);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      order
        ? {
            ...order,
            price: centsToEuro(order.price),
            startDate: order.startDate ? dayjs(order.startDate) : null,
            endDate: order.endDate ? dayjs(order.endDate) : null,
            guarantees: (order.guarantees || []).map(g => ({
              ...g,
              amount: centsToEuro(g.amount),
              validUntil: g.validUntil ? dayjs(g.validUntil) : null
            }))
          }
        : { retentionPercent: 5, guarantees: [], status: "active" }
    );
  }, [open, order?._id]);

  const submit = async values => {
    const body = {
      ...values,
      subcontractor: sub._id,
      price: euroToCents(values.price),
      startDate: day(values.startDate),
      endDate: day(values.endDate),
      guarantees: (values.guarantees || []).map(g => ({
        ...g,
        amount: euroToCents(g.amount),
        validUntil: day(g.validUntil)
      }))
    };
    setBusy(true);
    try {
      let res;
      if (order) res = await updateOrder(order._id, body);
      else {
        try {
          res = await createOrder(body);
        } catch (error) {
          if (!isWarning(error)) throw error;
          const go = await confirmWarnings(
            t,
            t("The subcontractor's documents are not complete"),
            error.response.data.check.warnings
          );
          if (!go) return;
          res = await createOrder({ ...body, acknowledge: true });
        }
      }
      Success(t("Saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={760}
      title={order ? order.number : t("New order")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="project" label={t("Project")} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="status" label={t("Status")}>
              <Select
                options={Object.entries(statuses).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="title" label={t("Order")} rules={[{ required: true }]}>
              <Input maxLength={200} placeholder={t("e.g. drywall 1st floor")} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="scope" label={t("Scope of work")}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} maxLength={10000} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="price" label={t("Price (net)")}>
              <InputNumber className="sc-full" min={0} precision={2} addonAfter="€" {...money} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="startDate" label={t("Start")}>
              <DatePicker className="sc-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="endDate" label={t("End")}>
              <DatePicker className="sc-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="retentionPercent" label={t("Retention")}>
              <InputNumber
                className="sc-full"
                min={0}
                max={30}
                precision={2}
                addonAfter="%"
                {...money}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.List name="guarantees">
          {(fields, { add, remove }) => (
            <>
              {fields.map(field => (
                <div key={field.key} className="sc-line">
                  <Row gutter={[12, 0]}>
                    <Col xs={24} md={8}>
                      <Form.Item name={[field.name, "kind"]} label={t("Guarantee")}>
                        <Select
                          options={Object.entries(kinds).map(([value, label]) => ({
                            value,
                            label
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name={[field.name, "amount"]} label={t("Amount")}>
                        <InputNumber
                          className="sc-full"
                          min={0}
                          precision={2}
                          addonAfter="€"
                          {...money}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item name={[field.name, "issuer"]} label={t("Bank or insurer")}>
                        <Input maxLength={200} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={8}>
                      <Form.Item name={[field.name, "validUntil"]} label={t("Valid until")}>
                        <DatePicker className="sc-full" format="DD.MM.YYYY" />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={8}>
                      <Form.Item name={[field.name, "received"]} valuePropName="checked" label=" ">
                        <Checkbox>{t("Received")}</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8} className="sc-line-actions">
                      <Button icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                        {t("Remove")}
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() => add({ kind: "performance", received: false })}
              >
                {t("Add guarantee")}
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

/** Release an Abschlagszahlung: shows retention, § 48 withholding and the payout first. */
export const PaymentModal = ({ open, order, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ date: dayjs() });
      setPreview(null);
    }
  }, [open]);

  const input = values => ({
    gross: euroToCents(values.gross),
    date: day(values.date),
    invoiceNumber: values.invoiceNumber,
    note: values.note
  });

  const check = async () => {
    setBusy("check");
    try {
      const values = await form.validateFields();
      const res = await previewPayment(order._id, input(values));
      setPreview(res.data);
    } catch (error) {
      if (error?.response) generalErrorAlert(error);
    } finally {
      setBusy("");
    }
  };

  const release = async () => {
    setBusy("release");
    try {
      const values = await form.validateFields();
      const res = await releasePayment(order._id, {
        ...input(values),
        // The warnings are on screen in the preview; releasing acknowledges them
        acknowledge: Boolean(preview?.check.warnings.length)
      });
      Success(t("Payment released"));
      onSaved(res.data.data);
    } catch (error) {
      if (isWarning(error)) setPreview(error.response.data);
      else if (error?.response) generalErrorAlert(error);
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={640}
      title={t("Release payment")}
      onCancel={onClose}
      footer={
        <Space wrap>
          <Button onClick={onClose}>{t("Cancel")}</Button>
          <Button onClick={check} loading={busy === "check"}>
            {t("Check")}
          </Button>
          <Button
            type="primary"
            danger={Boolean(preview?.check.warnings.length)}
            disabled={!preview}
            loading={busy === "release"}
            onClick={release}
          >
            {preview?.check.warnings.length ? t("Release despite warnings") : t("Release")}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onValuesChange={() => setPreview(null)}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="gross"
              label={t("Invoice amount (gross)")}
              rules={[{ required: true }]}
            >
              <InputNumber
                className="sc-full"
                min={0.01}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(lng)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="date" label={t("Payment date")} rules={[{ required: true }]}>
              <DatePicker className="sc-full" format="DD.MM.YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="invoiceNumber" label={t("Invoice number")}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="note" label={t("Note")}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {preview && (
        <>
          {preview.check.warnings.length > 0 && (
            <Alert
              className="mb-3"
              type={preview.check.withholding.withhold ? "error" : "warning"}
              showIcon
              message={t("Please check before paying")}
              description={<WarningList warnings={preview.check.warnings} />}
            />
          )}
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t("Invoice amount (gross)")}>
              <span className="sc-num">{formatEuro(preview.breakdown.gross, lng)}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("Retention ({{p}} %)", { p: order.retentionPercent })}>
              <span className="sc-num">− {formatEuro(preview.breakdown.retention, lng)}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("Bauabzugsteuer 15 % (to the Finanzamt)")}>
              <span className="sc-num">− {formatEuro(preview.breakdown.withheld, lng)}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("Payout to the subcontractor")}>
              <strong className="sc-num">{formatEuro(preview.breakdown.payout, lng)}</strong>
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Modal>
  );
};
